import React from 'react';
import { Box, Button, Chip, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';

function PanelHeader({ title, subtitle, badge }) {
    return (
        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                    <Typography component="h2" sx={{ fontSize: 15, lineHeight: 1.25, fontWeight: 850 }}>{title}</Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.2, color: 'text.secondary' }}>{subtitle}</Typography>
                </Box>
                {badge ? <Chip size="small" label={badge} sx={{ height: 24, flexShrink: 0, fontSize: 11, fontWeight: 750 }} /> : null}
            </Stack>
        </Box>
    );
}

function GateStep({ step, last }) {
    const actionable = step.available && step.onClick;
    return (
        <Box sx={{ display: 'grid', justifyItems: 'center' }}>
            <Button
                fullWidth
                variant={step.complete ? 'outlined' : 'contained'}
                color={step.complete ? 'success' : 'primary'}
                onClick={actionable ? step.onClick : undefined}
                disabled={!actionable}
                startIcon={step.complete ? <CheckCircleRoundedIcon /> : (!step.available ? <LockOutlinedIcon /> : <RadioButtonUncheckedRoundedIcon />)}
                sx={{
                    minHeight: 46,
                    justifyContent: 'flex-start',
                    px: 1.5,
                    textTransform: 'none',
                    fontWeight: 800,
                    ...(step.complete ? {
                        '&.Mui-disabled': {
                            color: 'success.main',
                            borderColor: 'success.main',
                            opacity: 1
                        }
                    } : {})
                }}
            >
                <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                    <Typography component="span" sx={{ display: 'block', fontSize: 13, fontWeight: 850, lineHeight: 1.2 }}>
                        {step.label}
                    </Typography>
                    <Typography component="span" variant="caption" sx={{ display: 'block', mt: 0.25, color: 'inherit', opacity: 0.76 }}>
                        {step.status}
                    </Typography>
                </Box>
            </Button>
            {!last ? <Box sx={{ width: 1, height: 18, backgroundColor: 'divider' }} /> : null}
        </Box>
    );
}

function CaptureMethod({ method, locked }) {
    const disabled = locked || method.disabled;
    return (
        <Button
            variant="outlined"
            onClick={disabled ? undefined : method.onClick}
            disabled={disabled}
            sx={{
                minHeight: 82,
                p: 1.5,
                borderRadius: 1,
                justifyContent: 'flex-start',
                textAlign: 'left',
                textTransform: 'none',
                borderColor: 'divider',
                backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.06 : 0.025)
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    {disabled ? <LockOutlinedIcon sx={{ fontSize: 16 }} /> : <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: 'primary.main' }} />}
                    <Typography sx={{ fontSize: 13, fontWeight: 850 }}>{method.label}</Typography>
                </Stack>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.7, color: 'text.secondary', lineHeight: 1.35 }}>
                    {locked ? method.lockedLabel : method.detail}
                </Typography>
            </Box>
        </Button>
    );
}

function ReadinessPanel({ readiness, generation, onOpenRisk }) {
    return (
        <Paper component="section" variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden', boxShadow: 'none', height: '100%' }}>
            <PanelHeader title="Generation readiness" subtitle="Information coverage and report output" badge={readiness.badge} />
            <Box sx={{ p: { xs: 1.5, sm: 2 }, display: 'grid', gap: 1.5 }}>
                <LinearProgress variant="determinate" value={readiness.coverage} color={readiness.blocked ? 'warning' : 'success'} sx={{ height: 7, borderRadius: 1 }} />
                <Box>
                    {readiness.rows.map((row, index) => (
                        <Box
                            key={row.label}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                                gap: 1,
                                alignItems: 'center',
                                py: 1.15,
                                borderTop: index ? '1px solid' : 'none',
                                borderColor: 'divider'
                            }}
                        >
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: row.tone === 'warning' ? 'warning.dark' : row.tone === 'success' ? 'success.main' : 'text.disabled' }} />
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.label}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'right' }}>{row.value}</Typography>
                        </Box>
                    ))}
                </Box>

                <Stack spacing={1}>
                    <Button fullWidth variant="contained" onClick={generation.onGenerate} disabled={!generation.canGenerate || generation.generating}>
                        {generation.generating ? 'Generating...' : generation.generateLabel}
                    </Button>
                    <Button fullWidth variant="outlined" onClick={generation.onOpen} disabled={!generation.canOpen || generation.opening}>
                        {generation.opening ? 'Opening...' : generation.openLabel}
                    </Button>
                    <Button fullWidth variant="outlined" onClick={generation.onIssue} disabled={!generation.canIssue}>
                        {generation.issueLabel}
                    </Button>
                    <Button fullWidth variant="text" color="warning" onClick={onOpenRisk}>Open Risk Tracker</Button>
                </Stack>
            </Box>
        </Paper>
    );
}

export default function ReportingPeriodWorkspace({ periods, gates, captureMethods, readiness, generation, onSelectPeriod, onOpenRisk }) {
    const captureLocked = gates.some((gate) => !gate.complete);
    const activePeriod = periods.find((period) => period.active);

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.8fr) minmax(310px, 1fr)' }, gap: 1.5, alignItems: 'stretch' }}>
            <Paper component="section" variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden', boxShadow: 'none' }}>
                <PanelHeader title="Report workspace" subtitle="Confirmations and data sources" badge={activePeriod?.label} />
                <Box sx={{ p: { xs: 1.5, sm: 2 }, display: 'grid', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 0.5, p: 0.4, borderRadius: 1, backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.045), overflowX: 'auto' }}>
                        {periods.map((period) => (
                            <Button
                                key={period.key}
                                size="small"
                                onClick={() => onSelectPeriod(period)}
                                disabled={period.disabled}
                                sx={{
                                    minWidth: 118,
                                    flex: periods.length <= 3 ? 1 : '0 0 auto',
                                    borderRadius: 0.75,
                                    color: period.active ? 'text.primary' : 'text.secondary',
                                    backgroundColor: period.active ? 'background.paper' : 'transparent',
                                    boxShadow: period.active ? '0 1px 4px rgba(15, 23, 42, 0.08)' : 'none'
                                }}
                            >
                                {period.label}
                            </Button>
                        ))}
                    </Box>

                    <Box>
                        <Typography sx={{ mb: 1, fontSize: 12, fontWeight: 850, color: 'text.secondary', textTransform: 'uppercase' }}>
                            Required confirmations
                        </Typography>
                        <Box sx={{ maxWidth: 620, mx: 'auto' }}>
                            {gates.map((step, index) => <GateStep key={step.key} step={step} last={index === gates.length - 1} />)}
                        </Box>
                    </Box>

                    <Box>
                        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 850, color: 'text.secondary', textTransform: 'uppercase' }}>
                                Data capture methods
                            </Typography>
                            {captureLocked ? <Chip size="small" icon={<LockOutlinedIcon />} label="Complete confirmations first" /> : null}
                        </Stack>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
                            {captureMethods.map((method) => <CaptureMethod key={method.key} method={method} locked={captureLocked} />)}
                        </Box>
                    </Box>
                </Box>
            </Paper>

            <ReadinessPanel readiness={readiness} generation={generation} onOpenRisk={onOpenRisk} />
        </Box>
    );
}